import React, { useContext, useEffect, useState } from "react";
import SectionHeader from "../components/section/SectionHeader";
import {
  INVENTORY_TABLE_HEADERS,
  VIEW_INVENTORY_FORM_FIELDS,
} from "../constants";
import AuthContext from "../store/authContext";
import {
  errorAlert,
  makeApiRequest,
  successAlert,
  transformInventoryResponseRow,
  transformInventoryRows,
} from "../util";
import DataTableWithControls from "../components/dataTable/DataTableWithControls";
import TableActionButton from "../ui/TableActionButton";
import { getInventoryByRef } from "../api";
import ViewRow from "../components/dataTable/ViewRow";
import ToggleField from "../components/inputs/ToggleField";
import useAppTitle from "../hooks/useAppTitle";
import useHideSidePanel from "../hooks/useHideSidePanel";
import { Lang, Page, Operation } from "../translations/types";
import {
  TOGGLE_TRANSLATIONS,
  getPageTranslationText,
} from "../translations/util";

const translations = getPageTranslationText({
  lang: Lang.EN,
  page: Page.INVENTORY,
  op: Operation.READ,
});

const Inventory = () => {
  const {
    token,
    scopes: { fm_alarms: fmAlarms },
  } = useContext(AuthContext);
  useAppTitle("Inventory - IWF Config Admin Tool (ICAT) by AT&T");
  const [loading, setLoading] = useState(false);
  const { showSidePanel, handleToggleSidePanel } = useHideSidePanel();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [inventoryData, setInventoryData] = useState<any[]>([]);
  const [selectedConnectionId, setSelectedConnectionId] = useState<
    string | null
  >(null);
  const [numberOfRows, setNumberOfRows] = useState(0);
  const [numberOfPages, setNumberOfPages] = useState(0);
  const [searchQuery, setSearhQuery] = useState<{
    pageNum: number;
    pageSize: number;
  }>({
    pageSize: 20,
    pageNum: 1,
  });

  const fetchInventory = async () => {
    setLoading(true);
    try {
      const { pageNum, pageSize } = searchQuery;
      const queryParams = new URLSearchParams({
        page: `${pageNum}`,
        pageSize: `${pageSize}`,
      });
      const response = await makeApiRequest<{
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        Rows: Array<any>;
        NbrOfRows: number;
        NbrOfPages: number;
      }>({
        path: `/api/v1/app-config/fault-mgmt/inventory?${queryParams}`,
        token,
      });
      setNumberOfRows(response.NbrOfRows);
      setNumberOfPages(response.NbrOfPages);
      setInventoryData(response.Rows);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
    } finally {
      setLoading(false);
    }
  };

  const toggleInventory = async (id: string, op: string) => {
    setLoading(true);
    try {
      await makeApiRequest<{
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        Rows: Array<any>;
        NbrOfRows: number;
        NbrOfPages: number;
      }>({
        path: `/api/v1/app-config/fault-mgmt/inventory/${id}/${op}`,
        token,
        method: "PUT",
        emptyResponse: true,
      });
      setInventoryData([]);
      successAlert(`Inventory ${op}ed`);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
    } finally {
      fetchInventory();
    }
  };

  useEffect(() => {
    fetchInventory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  return (
    <>
      <SectionHeader
        pageTitle={translations.header}
        pageDescription={translations.headerSubtext}
        sideComponent={null}
      />

      {showSidePanel && selectedConnectionId ? (
        <ViewRow
          panelName={translations.viewRow.panelName}
          org={selectedConnectionId}
          esRef={selectedConnectionId}
          setShowEditPanel={handleToggleSidePanel}
          formFields={VIEW_INVENTORY_FORM_FIELDS}
          transformResponseRow={transformInventoryResponseRow}
          loadDataByRef={getInventoryByRef}
        />
      ) : null}

      <DataTableWithControls
        loading={loading}
        showTableControls={false}
        filterColumns={[]}
        selectedColumn={null}
        setSelectedColumn={() => null}
        onSearch={() => fetchInventory()}
        showCreateButton={false}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        rows={transformInventoryRows(inventoryData).map((row: any) => ({
          ...row,
          state: (
            <ToggleField
              // eslint-disable-next-line no-unneeded-ternary
              value={row["INHIBITED-AT"] ? true : false}
              labelText=""
              disabled={
                row["EVENT-TYPE"] === "FAULT" || !fmAlarms.includes("u")
              }
              id={row["EVENT-ID"]}
              onChange={() => {
                if (row["INHIBITED-AT"]) {
                  toggleInventory(
                    row["EVENT-ID"],
                    TOGGLE_TRANSLATIONS["un-suppress"]
                  );
                } else {
                  toggleInventory(
                    row["EVENT-ID"],
                    TOGGLE_TRANSLATIONS.suppress
                  );
                }
              }}
            />
          ),
          action: (
            <TableActionButton
              onActionButtonClick={() => {
                setSelectedConnectionId(row.id);
                handleToggleSidePanel();
              }}
            />
          ),
        }))}
        headers={INVENTORY_TABLE_HEADERS}
        pageNum={searchQuery.pageNum}
        setPageNum={(pageNum) => setSearhQuery({ ...searchQuery, pageNum })}
        totalItems={numberOfRows}
        totalPages={numberOfPages}
        pageSize={searchQuery.pageSize}
        setPageSize={(pageSize) => setSearhQuery({ ...searchQuery, pageSize })}
      />
    </>
  );
};

export default Inventory;
